import React, { useState, useEffect, useRef, createContext, useContext, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import PasswordGate from "./components/PasswordGate";

// API Configuration
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("token"));

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check
    // AuthCallback will exchange the session_id and establish the session first
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true,
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
      setToken(null);
      localStorage.removeItem("token");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    setToken(access_token);
    setUser(userData);
    localStorage.setItem("token", access_token);
    return userData;
  };

  const register = async (data) => {
    const response = await axios.post(`${API}/auth/register`, data);
    const { access_token, user: userData } = response.data;
    setToken(access_token);
    setUser(userData);
    localStorage.setItem("token", access_token);
    return userData;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error("Logout error:", error);
    }
    setUser(null);
    setToken(null);
    localStorage.removeItem("token");
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  return (
    <AuthContext.Provider
      value={{ user, loading, token, login, register, loginWithGoogle, logout, updateUser, checkAuth }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { updateUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);

      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        try {
          const response = await axios.post(
            `${API}/auth/session`,
            { session_id: sessionId },
            { withCredentials: true }
          );
          updateUser(response.data.user);

          if (response.data.user.needs_profile_completion) {
            navigate("/complete-profile", { state: { user: response.data.user } });
          } else {
            navigate("/dashboard", { state: { user: response.data.user } });
          }
        } catch (error) {
          console.error("Auth callback error:", error);
          toast.error("Authentication failed");
          navigate("/login");
        }
      } else {
        navigate("/login");
      }
    };

    processAuth();
  }, [location, navigate, updateUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FEEE]">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-[#CCFF00] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-[#4A6B4A]">Authenticating...</p>
      </div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FEEE]">
        <div className="w-12 h-12 border-4 border-[#C50BEF] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// App Router
const AppRouter = () => {
  const location = useLocation();

  // Check for session_id in URL (OAuth callback)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/complete-profile" element={<CompleteProfilePage />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/upload"
        element={
          <ProtectedRoute>
            <UploadReceiptPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/deals"
        element={<DealsPage />}
      />
      <Route
        path="/wallet"
        element={
          <ProtectedRoute>
            <WalletPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/history"
        element={
          <ProtectedRoute>
            <HistoryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dispensaries"
        element={<DispensariesPage />}
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/verify-id"
        element={
          <ProtectedRoute>
            <VerifyIDPage />
          </ProtectedRoute>
        }
      />
      <Route path="/brand-solutions" element={<BrandSolutionsPage />} />
      
      {/* Company Pages */}
      <Route path="/about" element={<AboutPage />} />
      <Route path="/careers" element={<CareersPage />} />
      <Route path="/news" element={<NewsPage />} />
      <Route path="/contact" element={<ContactPage />} />
      
      {/* Brand Portal Routes */}
      <Route path="/brand/login" element={<BrandLoginPage />} />
      <Route path="/brand/dashboard" element={<BrandDashboardPage />} />
      <Route path="/brand/campaigns/new" element={<BrandCampaignPage />} />
      <Route path="/brand/campaigns/:campaignId" element={<BrandCampaignPage />} />
      
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

// Lazy load pages
const LandingPage = React.lazy(() => import("./pages/LandingPage"));
const LoginPage = React.lazy(() => import("./pages/LoginPage"));
const RegisterPage = React.lazy(() => import("./pages/RegisterPage"));
const CompleteProfilePage = React.lazy(() => import("./pages/CompleteProfilePage"));
const DashboardPage = React.lazy(() => import("./pages/DashboardPage"));
const UploadReceiptPage = React.lazy(() => import("./pages/UploadReceiptPage"));
const DealsPage = React.lazy(() => import("./pages/DealsPage"));
const WalletPage = React.lazy(() => import("./pages/WalletPage"));
const HistoryPage = React.lazy(() => import("./pages/HistoryPage"));
const DispensariesPage = React.lazy(() => import("./pages/DispensariesPage"));
const ProfilePage = React.lazy(() => import("./pages/ProfilePage"));
const VerifyIDPage = React.lazy(() => import("./pages/VerifyIDPage"));
const BrandSolutionsPage = React.lazy(() => import("./pages/BrandSolutionsPage"));
const BrandLoginPage = React.lazy(() => import("./pages/BrandLoginPage"));
const BrandDashboardPage = React.lazy(() => import("./pages/BrandDashboardPage"));
const BrandCampaignPage = React.lazy(() => import("./pages/BrandCampaignPage"));
const AboutPage = React.lazy(() => import("./pages/AboutPage"));
const CareersPage = React.lazy(() => import("./pages/CareersPage"));
const NewsPage = React.lazy(() => import("./pages/NewsPage"));
const ContactPage = React.lazy(() => import("./pages/ContactPage"));

function App() {
  return (
    <PasswordGate>
      <BrowserRouter>
        <AuthProvider>
          <React.Suspense
            fallback={
              <div className="min-h-screen flex items-center justify-center bg-[#F8FEEE]">
                <div className="w-12 h-12 border-4 border-[#C50BEF] border-t-transparent rounded-full animate-spin"></div>
              </div>
            }
          >
            <AppRouter />
          </React.Suspense>
          <Toaster
            position="top-right"
            toastOptions={{
              style: {
                background: "#0A120E",
                border: "1px solid rgba(255,255,255,0.1)",
                color: "#F0FDF4",
              },
            }}
          />
        </AuthProvider>
      </BrowserRouter>
    </PasswordGate>
  );
}

export default App;
export { API, BACKEND_URL };
